/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * Sets the Cache-Control header on an SSR response.
 *
 * Pages hand-rolled this with `res.set('Cache-Control', ...)`; the home page lost its header
 * entirely by passing a single argument, so it goes through one helper now.
 *
 * `uncacheable` exists because a render that lost a critical query still returns 200 (PWA Kit
 * swallows SSR fetch errors), and the CDN will happily serve that broken page to everyone until it
 * expires. Marking it `no-store` limits the damage to the one request that failed.
 *
 * Safe to call more than once per render — Express `res.set` overwrites, so the last call wins.
 *
 * @param {Object} res - Express response from `useServerContext()`. No-op when absent (client side).
 * @param {Object} [options]
 * @param {number} [options.maxAge] - `s-maxage` in seconds.
 * @param {number} [options.staleWhileRevalidate] - `stale-while-revalidate` in seconds.
 * @param {boolean} [options.uncacheable] - When true, emits `no-store` and ignores the other options.
 */
export const setCacheControl = (res, {maxAge, staleWhileRevalidate, uncacheable = false} = {}) => {
    if (!res?.set) return

    res.set(
        'Cache-Control',
        uncacheable
            ? 'no-store'
            : `s-maxage=${maxAge}, stale-while-revalidate=${staleWhileRevalidate}`
    )
}
